import React, { useState } from 'react';
import { Card, Btn, Badge, Modal, FormRow, EmptyState } from '../components/UI';
import { format, parseISO, isPast, isToday } from 'date-fns';

const PRIORITIES = ['Low', 'Medium', 'High'];

function priorityColor(p) {
  if (p === 'High') return 'red';
  if (p === 'Medium') return 'amber';
  return 'stone';
}

function dueBadge(task) {
  if (!task.dueDate || task.done) return null;
  try {
    const d = parseISO(task.dueDate);
    if (isPast(d) && !isToday(d)) return <Badge color="red">Overdue</Badge>;
    if (isToday(d)) return <Badge color="amber">Due today</Badge>;
  } catch {}
  return null;
}

function AddTaskModal({ members, onSave, onClose }) {
  const [form, setForm] = useState({ title: '', notes: '', assignee: '', dueDate: '', priority: 'Medium' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function handleSave() {
    if (!form.title.trim()) return;
    onSave({ id: 'task_' + Date.now(), ...form, done: false, createdAt: new Date().toISOString() });
  }

  return (
    <Modal title="Add task" onClose={onClose} width={420}>
      <FormRow label="Task">
        <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Water tomatoes in greenhouse" autoFocus />
      </FormRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormRow label="Assignee">
          <select value={form.assignee} onChange={e => set('assignee', e.target.value)}>
            <option value="">— Unassigned —</option>
            {(members || []).map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Due date">
          <input type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
        </FormRow>
      </div>
      <FormRow label="Priority">
        <div style={{ display: 'flex', gap: 8 }}>
          {PRIORITIES.map(p => (
            <button key={p} onClick={() => set('priority', p)} style={{
              flex: 1, padding: '6px 0', fontSize: 13, borderRadius: 'var(--radius-sm)',
              border: '1px solid', cursor: 'pointer',
              borderColor: form.priority === p ? 'var(--accent)' : 'var(--border-md)',
              background: form.priority === p ? 'var(--accent-light)' : 'transparent',
              color: form.priority === p ? 'var(--accent)' : 'var(--text-secondary)',
            }}>{p}</button>
          ))}
        </div>
      </FormRow>
      <FormRow label="Notes">
        <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any details…" />
      </FormRow>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={handleSave} disabled={!form.title.trim()}>Add task</Btn>
      </div>
    </Modal>
  );
}

export default function Tasks({ state, dispatch }) {
  const [addModal, setAddModal] = useState(false);
  const [filter, setFilter] = useState('pending');
  const { tasks = [], members = [] } = state;

  const filters = [
    { key: 'pending', label: 'Pending', count: tasks.filter(t => !t.done).length },
    { key: 'done', label: 'Done', count: tasks.filter(t => t.done).length },
    { key: 'all', label: 'All', count: tasks.length },
  ];

  const filtered = tasks.filter(t => {
    if (filter === 'pending') return !t.done;
    if (filter === 'done') return t.done;
    return true;
  }).sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    const pri = { High: 0, Medium: 1, Low: 2 };
    return (pri[a.priority] ?? 1) - (pri[b.priority] ?? 1);
  });

  return (
    <div style={{ padding: '2rem', maxWidth: 700 }} className="fade-up">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: 24, marginBottom: 4 }}>Tasks</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{tasks.filter(t => !t.done).length} pending</p>
        </div>
        <Btn variant="primary" onClick={() => setAddModal(true)}>+ Add task</Btn>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: '1.25rem' }}>
        {filters.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            fontSize: 13, padding: '5px 14px', borderRadius: 20, border: '1px solid', cursor: 'pointer',
            borderColor: filter === f.key ? 'var(--accent)' : 'var(--border-md)',
            background: filter === f.key ? 'var(--accent-light)' : 'transparent',
            color: filter === f.key ? 'var(--accent)' : 'var(--text-secondary)',
          }}>{f.label} <span style={{ opacity: 0.6 }}>({f.count})</span></button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="✓" title={filter === 'pending' ? 'All caught up' : 'No tasks yet'}
          body={filter === 'pending' ? 'No pending tasks.' : 'Add your first task to get started.'}
          action={filter !== 'done' ? <Btn variant="primary" onClick={() => setAddModal(true)}>Add task</Btn> : null} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {filtered.map(task => (
            <div key={task.id} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '0.875rem 1rem',
              display: 'flex', alignItems: 'flex-start', gap: 12,
              opacity: task.done ? 0.6 : 1, transition: 'opacity 0.15s',
            }}>
              <button onClick={() => dispatch({ type: 'TOGGLE_TASK', id: task.id })}
                style={{
                  width: 20, height: 20, borderRadius: 5, marginTop: 1,
                  border: `2px solid ${task.done ? 'var(--accent)' : 'var(--border-md)'}`,
                  background: task.done ? 'var(--accent)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0, color: 'white', fontSize: 12,
                }}>{task.done ? '✓' : ''}</button>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: task.notes ? 4 : 0 }}>
                  <span style={{
                    fontSize: 14, fontWeight: 500,
                    textDecoration: task.done ? 'line-through' : 'none',
                    color: task.done ? 'var(--text-tertiary)' : 'var(--text-primary)',
                  }}>{task.title}</span>
                  {!task.done && task.priority && task.priority !== 'Low' && (
                    <Badge color={priorityColor(task.priority)}>{task.priority}</Badge>
                  )}
                  {dueBadge(task)}
                </div>
                {task.notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{task.notes}</div>}
                <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 11, color: 'var(--text-tertiary)' }}>
                  {task.assignee && <span>→ {task.assignee}</span>}
                  {task.dueDate && <span>Due {format(parseISO(task.dueDate), 'd MMM')}</span>}
                </div>
              </div>

              <button onClick={() => dispatch({ type: 'DELETE_TASK', id: task.id })}
                style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '0 2px', flexShrink: 0 }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {addModal && (
        <AddTaskModal
          members={members}
          onSave={task => { dispatch({ type: 'ADD_TASK', task }); setAddModal(false); }}
          onClose={() => setAddModal(false)}
        />
      )}
    </div>
  );
}
